// Similar a EditarPago pero para Clientes
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, Plus, Trash2 } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import StickyActions from '../../../components/StickyActions';
import SelectorClienteProveedor from '../../../components/Tesoreria/SelectorClienteProveedor';
import SelectorComprobantes from '../../../components/Tesoreria/SelectorComprobantes';
import SelectorMedioPago from '../../../components/Tesoreria/SelectorMedioPago';
import SelectorCuenta from '../../../components/Tesoreria/SelectorCuenta';
import { useCobranza, useCobranzaMutations } from '../../../graphql/tesoreria';

export default function EditarCobranza() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    clienteId: '',
    cliente: null,
    moneda: 'ARS',
    medioPagoId: '',
    cuentaBancariaId: '',
    observaciones: '',
    imputaciones: [],
    adjuntos: [],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showSelectorCliente, setShowSelectorCliente] = useState(false);
  const [showSelectorComprobantes, setShowSelectorComprobantes] = useState(false);
  const [adjuntoUrl, setAdjuntoUrl] = useState('');

  const [result] = useCobranza(id);
  const { createCobranza, updateCobranza } = useCobranzaMutations();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSubmit(e);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [formData]);

  const calcularTotal = () => formData.imputaciones.reduce((sum, imp) => sum + (imp.monto || 0), 0);
  const calcularEstado = () => {
    const total = calcularTotal();
    if (total === 0) return 'PENDIENTE';
    const haySaldo = formData.imputaciones.some((imp) => imp.saldoAnterior > imp.monto);
    return haySaldo ? 'PARCIAL' : 'CERRADO';
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fecha) newErrors.fecha = 'La fecha es obligatoria';
    if (!formData.clienteId) newErrors.clienteId = 'Debe seleccionar un cliente';
    if (!formData.medioPagoId) newErrors.medioPagoId = 'Debe seleccionar un medio de pago';
    if (formData.imputaciones.length === 0) newErrors.imputaciones = 'Debe imputar al menos un comprobante';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const input = { ...formData, importe: calcularTotal(), estado: calcularEstado() };
      if (isEditing) {
        await updateCobranza({ id, input });
        alert('Cobranza actualizada');
      } else {
        await createCobranza({ input });
        alert('Cobranza creada');
      }
      navigate('/Tesoreria/Cobranzas');
    } catch (error) {
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: formData.moneda || 'ARS' }).format(value);

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))] pb-24">
      <PageHeader title={isEditing ? 'Editar Cobranza' : 'Nueva Cobranza'} subtitle="Registrar cobro a cliente" />

      <section className="max-w-[1400px] mx-auto px-4 py-6 grid grid-cols-12 gap-4">
        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-6">
          <h3 className="text-lg font-semibold text-[rgb(var(--fg))] mb-4">Datos de la Cobranza</h3>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-4">
              <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">Fecha <span className="text-red-500">*</span></label>
              <input type="date" value={formData.fecha} onChange={(e) => setFormData({ ...formData, fecha: e.target.value })} className={`w-full px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${errors.fecha ? 'border-red-500' : 'border-[rgb(var(--border))]'}`} />
            </div>
            <div className="col-span-12 md:col-span-8">
              <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">Cliente <span className="text-red-500">*</span></label>
              <button type="button" onClick={() => setShowSelectorCliente(true)} className={`w-full px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-left focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${errors.clienteId ? 'border-red-500' : 'border-[rgb(var(--border))]'}`}>
                {formData.cliente ? <span className="text-[rgb(var(--fg))]">{formData.cliente.nombre}</span> : <span className="text-[rgb(var(--muted))]">Seleccionar cliente</span>}
              </button>
            </div>
            <div className="col-span-12 md:col-span-4">
              <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">Moneda</label>
              <select value={formData.moneda} onChange={(e) => setFormData({ ...formData, moneda: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]">
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div className="col-span-12 md:col-span-4">
              <SelectorMedioPago value={formData.medioPagoId} onChange={(m) => setFormData({ ...formData, medioPagoId: m?.id || '' })} label="Medio de Cobro" required error={errors.medioPagoId} />
            </div>
            <div className="col-span-12 md:col-span-4">
              <SelectorCuenta value={formData.cuentaBancariaId} onChange={(c) => setFormData({ ...formData, cuentaBancariaId: c?.id || '' })} label="Cuenta Bancaria" />
            </div>
            <div className="col-span-12">
              <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">Observaciones</label>
              <textarea value={formData.observaciones} onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} rows={2} className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] resize-none" />
            </div>
          </div>
        </div>

        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[rgb(var(--fg))]">Imputación</h3>
            <button onClick={() => setShowSelectorComprobantes(true)} disabled={!formData.clienteId} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] disabled:opacity-50 transition-colors">
              <Plus className="w-4 h-4" />
              Agregar
            </button>
          </div>
          {formData.imputaciones.length > 0 ? (
            <table className="w-full">
              <thead className="bg-[rgba(var(--subpanel))] border-b border-[rgb(var(--border))]">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Comprobante</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">Saldo</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">Imputado</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--border))]">
                {formData.imputaciones.map((imp, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm text-[rgb(var(--fg))]">{imp.comprobanteNumero}</td>
                    <td className="px-4 py-3 text-sm text-right text-[rgb(var(--muted))]">{formatCurrency(imp.saldoAnterior || 0)}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-[rgb(var(--fg))]">{formatCurrency(imp.monto || 0)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setFormData({ ...formData, imputaciones: formData.imputaciones.filter((_, idx) => idx !== i) })} className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="bg-[rgba(var(--brand),0.05)] font-semibold">
                  <td colSpan={2} className="px-4 py-3 text-right text-[rgb(var(--fg))]">Total:</td>
                  <td className="px-4 py-3 text-right text-[rgb(var(--fg))]">{formatCurrency(calcularTotal())}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="text-center text-[rgb(var(--muted))] py-8">No hay comprobantes imputados</p>
          )}
        </div>
      </section>

      <StickyActions
        actions={[
          <button key="guardar" onClick={handleSubmit} disabled={saving} className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]">
            <Save className="w-4 h-4" />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>,
          <button key="cancelar" onClick={() => navigate('/Tesoreria/Cobranzas')} className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] transition-colors">
            <X className="w-4 h-4" />
            Cancelar
          </button>,
        ]}
      />

      <SelectorClienteProveedor isOpen={showSelectorCliente} onClose={() => setShowSelectorCliente(false)} onSelect={(cliente) => setFormData({ ...formData, clienteId: cliente.id, cliente })} mode="cliente" selectedId={formData.clienteId} />
      <SelectorComprobantes isOpen={showSelectorComprobantes} onClose={() => setShowSelectorComprobantes(false)} onConfirm={(imp) => setFormData({ ...formData, imputaciones: imp })} entidadId={formData.clienteId} mode="cliente" selected={formData.imputaciones} />
    </div>
  );
}
